"use client";

import { useRef, useState } from 'react';
import { RecorridoPunto } from '@/lib/types';
import { cn } from '@/lib/utils';
import { ZoomIn, ZoomOut, Maximize } from 'lucide-react';

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

export function pointsDistance(a?: RecorridoPunto | null, b?: RecorridoPunto | null) {
  if (!a || !b) return Infinity;
  return Math.hypot(a.x - b.x, a.y - b.y);
}

export const ENDPOINT_TOLERANCE = 6;

export function isNear(a?: RecorridoPunto | null, b?: RecorridoPunto | null, tolerance = ENDPOINT_TOLERANCE) {
  return pointsDistance(a, b) <= tolerance;
}

export const CARRERA_OFICIAL_COLOR = '#b91c1c';

/** El dorado de la web (`--primary`, hsl 48 96% 62%) en hexadecimal. */
export const JARTI_AMARILLO = '#fbd641';

export function darkenColor(hex: string, amount = 0.35): string {
  if (!hex?.startsWith('#') || hex.length < 7) return hex;
  const r = Math.round(parseInt(hex.slice(1, 3), 16) * (1 - amount));
  const g = Math.round(parseInt(hex.slice(3, 5), 16) * (1 - amount));
  const b = Math.round(parseInt(hex.slice(5, 7), 16) * (1 - amount));
  return `#${[r, g, b].map((v) => clamp(v, 0, 255).toString(16).padStart(2, '0')).join('')}`;
}

function toPolylinePoints(puntos: RecorridoPunto[]) {
  return puntos.map((p) => `${p.x},${p.y}`).join(' ');
}

export interface RouteOverlay {
  puntos: RecorridoPunto[];
  color: string;
  dashed?: boolean;
}

export interface RouteMarker {
  point: RecorridoPunto;
  color: string;
  ring?: boolean;
  iconUrl?: string;
  /** Dibuja una cruz encima (estación de penitencia). */
  cross?: boolean;
  label?: string;
}

interface RouteMapEditorProps {
  mapImageUrl: string;
  onMapClick?: (point: RecorridoPunto) => void;
  reference?: RouteOverlay | null;
  referenceEndpointMarkers?: boolean;
  segments?: RouteOverlay[];
  showSegmentPoints?: boolean;
  /**
   * Tiradores gordos y clicables sobre cada punto de los tramos (modo edición):
   * al pulsar uno se avisa por `onSegmentPointClick` para poder borrarlo.
   */
  pointHandles?: boolean;
  onSegmentPointClick?: (segmentIndex: number, pointIndex: number) => void;
  markers?: RouteMarker[];
  catedral?: { point: RecorridoPunto } | null;
  className?: string;
  allowZoom?: boolean;
}

const ZOOM_MIN = 1;
const ZOOM_MAX = 8;
const ZOOM_STEP = 0.5;

export function RouteMapEditor({
  mapImageUrl,
  onMapClick,
  reference = null,
  referenceEndpointMarkers = false,
  segments = [],
  showSegmentPoints = true,
  pointHandles = false,
  onSegmentPointClick,
  markers = [],
  catedral = null,
  className,
  allowZoom = true,
}: RouteMapEditorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(1);

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!onMapClick || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clamp(((e.clientX - rect.left) / rect.width) * 100, 0, 100);
    const y = clamp(((e.clientY - rect.top) / rect.height) * 100, 0, 100);
    onMapClick({ x, y });
  };

  const renderMarker = (marker: RouteMarker, key: string | number) => {
    if (marker.iconUrl) {
      // El escudo va sobre un disco negro algo mayor con un filete del dorado
      // de la web: así se lee encima de cualquier zona del mapa.
      const size = 5;
      const radio = size * 0.68;
      return (
        <g key={key} style={{ filter: 'drop-shadow(0 0.4px 0.8px rgba(0,0,0,0.8))' }}>
          <circle cx={marker.point.x} cy={marker.point.y} r={radio} fill="#0a0a0c" stroke={JARTI_AMARILLO} strokeWidth={0.28} />
          <image
            href={marker.iconUrl}
            x={marker.point.x - size / 2}
            y={marker.point.y - size / 2}
            width={size}
            height={size}
            preserveAspectRatio="xMidYMid meet"
          />
        </g>
      );
    }
    return (
      <g key={key}>
        {marker.ring && <circle cx={marker.point.x} cy={marker.point.y} r={2.4} fill="none" stroke={marker.color} strokeWidth={0.3} opacity={0.6} />}
        <circle cx={marker.point.x} cy={marker.point.y} r={1.4} fill={marker.color} stroke="white" strokeWidth={0.35} />
        {marker.cross && (
          <>
            <rect x={marker.point.x - 0.14} y={marker.point.y - 0.95} width={0.28} height={1.9} fill="white" />
            <rect x={marker.point.x - 0.6} y={marker.point.y - 0.45} width={1.2} height={0.28} fill="white" />
          </>
        )}
      </g>
    );
  };

  return (
    <div className={cn('relative overflow-auto rounded-xl border border-white/10', !className?.includes('h-') && 'max-h-[70vh]', className)}>
      <div style={{ width: `${zoom * 100}%` }}>
        <div
          ref={containerRef}
          onClick={handleClick}
          className={cn('relative w-full select-none', onMapClick ? 'cursor-crosshair' : '')}
        >
          <img src={mapImageUrl} alt="Mapa" className="block h-full w-full object-cover" draggable={false} />
          <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 h-full w-full">
            {reference && reference.puntos.length >= 2 && (
              <polyline
                points={toPolylinePoints(reference.puntos)}
                fill="none"
                stroke={reference.color}
                strokeWidth={0.7}
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity={0.95}
              />
            )}

            {referenceEndpointMarkers && reference && reference.puntos.length >= 2 && (
              <>
                <circle cx={reference.puntos[0].x} cy={reference.puntos[0].y} r={1.3} fill={reference.color} stroke="white" strokeWidth={0.3} />
                <circle
                  cx={reference.puntos[reference.puntos.length - 1].x}
                  cy={reference.puntos[reference.puntos.length - 1].y}
                  r={1.3}
                  fill={reference.color}
                  stroke="white"
                  strokeWidth={0.3}
                />
              </>
            )}

            {segments.map((segment, i) => (
              <polyline
                key={i}
                points={toPolylinePoints(segment.puntos)}
                fill="none"
                stroke={segment.color}
                strokeWidth={0.6}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray={segment.dashed ? '2,1.4' : undefined}
                opacity={0.95}
              />
            ))}

            {showSegmentPoints &&
              !pointHandles &&
              segments.map((segment, si) =>
                segment.puntos.map((p, pi) => (
                  <circle key={`${si}-${pi}`} cx={p.x} cy={p.y} r={0.55} fill={segment.color} stroke="white" strokeWidth={0.2} />
                ))
              )}

            {/* Tiradores del modo edición: clic sobre uno lo borra. */}
            {pointHandles &&
              segments.map((segment, si) =>
                segment.puntos.map((p, pi) => (
                  <circle
                    key={`h-${si}-${pi}`}
                    cx={p.x}
                    cy={p.y}
                    r={1}
                    fill={segment.color}
                    stroke="white"
                    strokeWidth={0.3}
                    style={{ cursor: onSegmentPointClick ? 'pointer' : 'default' }}
                    onClick={(e) => {
                      if (!onSegmentPointClick) return;
                      e.stopPropagation();
                      onSegmentPointClick(si, pi);
                    }}
                  >
                    <title>{`Punto ${pi + 1} — clic para borrarlo`}</title>
                  </circle>
                ))
              )}

            {markers.map((marker, i) => renderMarker(marker, i))}

            {catedral && (
              <g>
                <circle cx={catedral.point.x} cy={catedral.point.y} r={1.6} fill="#eab308" stroke="white" strokeWidth={0.35} />
                <rect x={catedral.point.x - 0.18} y={catedral.point.y - 1.1} width={0.36} height={2.2} fill="white" />
                <rect x={catedral.point.x - 0.7} y={catedral.point.y - 0.5} width={1.4} height={0.36} fill="white" />
              </g>
            )}
          </svg>
        </div>
      </div>

      {/* `data-solo-pantalla`: la ruta /render/programa-ss los oculta, porque en
          una carilla fotografiada para el librito del mod unos botones de zoom
          que nadie puede pulsar solo ocupan sitio. */}
      {allowZoom && (
        // `sticky` con las DOS coordenadas (`bottom` y `left`): con solo
        // `bottom` se quedaba pegado abajo pero se iba con el scroll
        // horizontal, así que al ampliar y mirar la derecha del mapa la lupa
        // desaparecía.
        <div data-solo-pantalla className="sticky bottom-2 left-2 z-30 ml-2 flex w-fit gap-1 rounded-lg border border-white/10 bg-background/80 p-1 backdrop-blur-sm">
          <button
            type="button"
            onClick={() => setZoom((z) => clamp(z - ZOOM_STEP, ZOOM_MIN, ZOOM_MAX))}
            disabled={zoom <= ZOOM_MIN}
            className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground disabled:opacity-30"
          >
            <ZoomOut className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => setZoom(1)}
            disabled={zoom === 1}
            className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground disabled:opacity-30"
          >
            <Maximize className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => setZoom((z) => clamp(z + ZOOM_STEP, ZOOM_MIN, ZOOM_MAX))}
            disabled={zoom >= ZOOM_MAX}
            className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground disabled:opacity-30"
          >
            <ZoomIn className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}
